import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import { Box, Button, Stack, Typography } from '@mui/material';
import React from 'react';
import { useHistory } from 'react-router-dom';
import VerbaScholaStudentPageContent from '../component/VerbaScholaStudentPageContent';
import VerbaScholaTeacherShell from '../component/VerbaScholaTeacherShell';
import { BLUE, BORDER, INTER, NAVY, POPPINS, SLATE } from '../style/verbaScholaStyles';
import { studentCardSx } from '../style/verbaScholaStudentStyles';

const studentTopics = [
    { title: 'A lesson is unavailable', detail: 'Your teacher may have hidden, unpublished, or archived it. Your saved work remains, and you can return if the lesson becomes available again.' },
    { title: 'Audio or recording is not working', detail: 'Check your device sound and browser microphone permission. Replay the model audio, then try recording again. If the audio still fails, return to your course and tell your teacher which lesson and activity you were using.' },
    { title: 'Find your progress or feedback', detail: 'Open My courses to continue a lesson or practice it again. Teacher feedback appears in its own section once your teacher sends it.' },
];
const teacherTopics = [
    { title: 'Students cannot open a lesson', detail: 'In the lesson overview, check that it is available to students. A hidden, unpublished, or archived lesson keeps student work but pauses access.' },
    { title: 'An activity is not ready to publish', detail: 'Open Exercises, review every Ready activity, and check the saved audio status. Student audio needs every required voice before the lesson can be made available.' },
    { title: 'Preview and saved changes', detail: 'Student preview uses the student player without saving attempts. Manual edits save in each exercise card; the printable plan includes only saved changes.' },
];

function HelpContent({ teacher }: { teacher: boolean }) {
    const history = useHistory();
    return <Stack spacing={2} sx={{ maxWidth: 820 }}>
        {(teacher ? teacherTopics : studentTopics).map(topic => <Box key={topic.title} sx={{ ...studentCardSx, p: { xs: 2.5, sm: 3 }, border: `1px solid ${BORDER}` }}>
            <Typography component="h2" sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 17, color: NAVY }}>{topic.title}</Typography>
            {/* eslint-disable-next-line no-restricted-syntax -- fixed help copy, not lesson text */}
            <Typography sx={{ mt: 0.75, fontFamily: INTER, fontSize: 14, lineHeight: 1.6, color: SLATE }}>{topic.detail}</Typography>
        </Box>)}
        <Box sx={{ ...studentCardSx, p: { xs: 2.5, sm: 3 }, border: `1px solid ${BORDER}` }}>
            <Typography component="h2" sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 17, color: NAVY }}>Still need help?</Typography>
            <Typography sx={{ mt: 0.75, fontFamily: INTER, fontSize: 14, lineHeight: 1.6, color: SLATE }}>
                {teacher ? 'Ask your school administrator for help with class access or a persistent technical problem.'
                    : 'Ask your teacher about a lesson or assignment. For sign-in or invitation trouble, ask your teacher or school administrator.'}
            </Typography>
            <Typography sx={{ mt: 1, fontFamily: INTER, fontSize: 14, color: SLATE }}>For product support, email support@verbaschola.com. Include the lesson name and what happened; do not send your password.</Typography>
            <Button component="a" href="mailto:support@verbaschola.com" startIcon={<EmailOutlinedIcon />}
                sx={{ mt: 1.5, minHeight: 44, color: BLUE, fontFamily: INTER, fontWeight: 700, textTransform: 'none' }}>Email support</Button>
            <Button onClick={() => history.push(teacher ? '/teacher' : '/student/courses')}
                sx={{ mt: 1.5, minHeight: 44, color: BLUE, fontFamily: INTER, fontWeight: 700, textTransform: 'none' }}>
                {teacher ? 'Back to classes' : 'Go to my courses'}
            </Button>
        </Box>
    </Stack>;
}

export function VerbaScholaTeacherHelpPage() {
    return <VerbaScholaTeacherShell title="Help & support" subtitle="Answers for teaching and lesson setup."
        backTo="/teacher" backLabel="Classes" loading={false} error={null}>
        <HelpContent teacher />
    </VerbaScholaTeacherShell>;
}

export function VerbaScholaStudentHelpPage() {
    return <VerbaScholaStudentPageContent maxWidth={900}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2.5 }}>
            <HelpOutlineRoundedIcon sx={{ color: BLUE }} />
            <Typography component="h1" sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: { xs: 25, sm: 29 }, color: NAVY }}>Help & support</Typography>
        </Stack>
        <HelpContent teacher={false} />
    </VerbaScholaStudentPageContent>;
}
